import React, { useState, useEffect } from 'react';
import { Plus, Trash2, List, Search, Download, Upload } from 'lucide-react';

export default function MachuchoDeckBuilder() {
  const [decks, setDecks] = useState([]);
  const [currentDeck, setCurrentDeck] = useState(null);
  const [deckName, setDeckName] = useState('');
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardsDatabase, setCardsDatabase] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [filterSet, setFilterSet] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterRace, setFilterRace] = useState('all');
  const [cardQuantity, setCardQuantity] = useState(1);
  const [showDropdown, setShowDropdown] = useState(false);

  // Card name corrector eg. "el_reto" -> "El Reto"
  const cleanName = (str) => {
    if (!str) return '';
    return str
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Load Races from cardDatabase
  const availableRaces = React.useMemo(() => {
    return [...new Set(cardsDatabase
      .map(card => card.race)
      .filter(race => race != null && race !== '')
    )].sort();
  }, [cardsDatabase]);

  // Load Sets from cardDatabase
  const availableSets = React.useMemo(() => {
    return [...new Set(cardsDatabase.map(card => card.set))].sort();
  }, [cardsDatabase]);

  // Load Decks from localStorage on mount
  useEffect(() => {
    const savedDecks = localStorage.getItem('machucho-decks');
    if (savedDecks) {
      try {
        setDecks(JSON.parse(savedDecks));
      } catch (error) {
        console.error('Error loading decks:', error);
      }
    }
  }, []);

  // Load cards database from JSON files
  useEffect(() => {
    const loadCards = async () => {
      try {
        const indexResponse = await fetch(`${process.env.PUBLIC_URL}/cards/index.json`);
        if (!indexResponse.ok) throw new Error(`index.json not found`);

        const indexText = await indexResponse.text();
        const indexData = JSON.parse(indexText);
        const allCards = [];

        for (const fileName of indexData.files) {
          try {
            const response = await fetch(`${process.env.PUBLIC_URL}/cards/${fileName}`);
            if (!response.ok) continue;

            const cardText = await response.text();
            const cards = JSON.parse(cardText);

            // Parse edition and set from filename
            const [edition, ...setParts] = fileName.replace('.json', '').split('-');
            const setName = setParts.join('-');
            // Add the edition and set to each card
            const cardsWithMeta = cards.map((card, index) => ({
              ...card,
              id: `${fileName}-${index}`,
              edition: edition,
              set: setName,
              fileName: fileName
            }));

            allCards.push(...cardsWithMeta);
          } catch (error) {
            console.error(`Error loading ${fileName}:`, error);
          }
        }

        setCardsDatabase(allCards);
        setLoading(false);
      } catch (error) {
        console.error('Error loading cards:', error);
        setLoading(false);
      }
    };

    loadCards();
  }, []);

  // Save decks to localStorage
  useEffect(() => {
    if (decks.length > 0) {
      localStorage.setItem('machucho-decks', JSON.stringify(decks));
    }
  }, [decks]);

  // Filter cards logic
  useEffect(() => {
    let filtered = cardsDatabase;

    // Search filter with diacritics ignore logic.
    if (searchTerm.trim()) {
      const normalize = (str) =>
        str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

      const normalizedSearch = normalize(searchTerm);

      filtered = filtered.filter(card =>
        typeof card.name === 'string' &&
        normalize(card.name).includes(normalizedSearch)
      );
    }

    // Filter by Set
    if (filterSet !== 'all') {
      filtered = filtered.filter(card => card.set === filterSet);
    }

    // Filter by Type
    if (filterType !== 'all') {
      filtered = filtered.filter(card =>
        typeof card.type === 'string' &&
        card.type.toLowerCase() === filterType.toLowerCase());
    }

    // Filter by Race
    if (filterRace !== 'all') {
      filtered = filtered.filter(card =>
        card.race && card.race.toLowerCase() === filterRace.toLowerCase());
    }

    setFilteredCards(filtered);
  }, [searchTerm, filterSet, filterType, filterRace, cardsDatabase]);

  const createDeck = () => {
    if (!deckName.trim()) return;
    const newDeck = {
      id: Date.now(),
      name: deckName,
      cards: [],
      createdAt: new Date().toISOString()
    };
    setDecks([...decks, newDeck]);
    setCurrentDeck(newDeck);
    setDeckName('');
  };

  const selectCard = (card) => {
    setSelectedCard(card);
    setSearchTerm(card.name);
    setShowDropdown(false);
    setCardQuantity(1);
  };

  const getMaxCopies = (rarity) => {
    if (rarity === 'Única') return 1;
    return 3;
  };

  const addCardToDeck = () => {
    if (!currentDeck || !selectedCard) return;

    const existingCard = currentDeck.cards.find(c => c.cardId === selectedCard.id);
    const maxCopies = getMaxCopies(selectedCard.rarity);
    const currentCopies = existingCard ? existingCard.quantity : 0;

    // Add more than 1 copy
    const allowedToAdd = Math.min(cardQuantity, maxCopies - currentCopies);

    if (allowedToAdd <= 0) {
      alert(`Ya tienes el máximo de copias permitidas (${maxCopies}) para esta carta.`);
      return;
    }

    if (getTotalCards() + allowedToAdd > 50) {
      alert('No puedes exceder 50 cartas en el mazo');
      return;
    }

    const newQuantity = currentCopies + allowedToAdd;

    const updatedCards = existingCard
      ? currentDeck.cards.map(c =>
          c.cardId === selectedCard.id ? { ...c, quantity: newQuantity } : c
        )
      : [...currentDeck.cards, {
          id: Date.now(),
          cardId: selectedCard.id,
          edition: selectedCard.edition,
          set: selectedCard.set,
          type: selectedCard.type,
          name: selectedCard.name,
          image_path: selectedCard.image_path,
          rarity: selectedCard.rarity,
          race: selectedCard.race,
          attack: selectedCard.attack,
          cost: selectedCard.cost,
          strategy: selectedCard.strategy,
          ability: selectedCard.ability,
          quantity: newQuantity
        }];

    const updatedDeck = { ...currentDeck, cards: updatedCards };
    setCurrentDeck(updatedDeck);
    setDecks(decks.map(d => d.id === currentDeck.id ? updatedDeck : d));

    // Reset form
    setSearchTerm('');
    setSelectedCard(null);
    setCardQuantity(1);
    setShowAddCard(false);
    setFilterSet('all');
    setFilterType('all');
  };

  const removeCard = (cardId) => {
    const updatedCards = currentDeck.cards.filter(c => c.id !== cardId);
    const updatedDeck = { ...currentDeck, cards: updatedCards };
    setCurrentDeck(updatedDeck);
    setDecks(decks.map(d => d.id === currentDeck.id ? updatedDeck : d));
  };

  const getTotalCards = () => {
    if (!currentDeck) return 0;
    return currentDeck.cards.reduce((sum, card) => sum + card.quantity, 0);
  };

  const getCardsByType = (type) => {
    if (!currentDeck) return [];
    return currentDeck.cards.filter(c => c.type && typeof c.type === 'string' && c.type.toLowerCase() === type.toLowerCase());
  };

  const cancelAddCard = () => {
    setShowAddCard(false);
    setSearchTerm('');
    setSelectedCard(null);
    setCardQuantity(1);
    setFilterSet('all');
    setFilterType('all');
  };

  const deleteDeck = (deckId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este mazo?')) {
      const updatedDecks = decks.filter(d => d.id !== deckId);
      setDecks(updatedDecks);
      if (updatedDecks.length === 0) {
        localStorage.removeItem('machucho-decks');
      }
    }
  };

  const exportDeck = () => {
    if (!currentDeck) return;

    const dataStr = JSON.stringify(currentDeck, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentDeck.name.replace(/\s+/g, '_')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importDeck = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedDeck = JSON.parse(e.target.result);

        // Validate deck structure
        if (!importedDeck.name || !Array.isArray(importedDeck.cards)) {
          alert('El archivo es inválido. Asegúrate de importar un archivo JSON de Machucho.');
          return;
        }

        // Generate new ID to avoid conflicts
        const newDeck = {
          ...importedDeck,
          id: Date.now(),
          createdAt: new Date().toISOString()
        };

        setDecks([...decks, newDeck]);
        alert(`Mazo "${newDeck.name}" importado exitosamente!`);
        event.target.value = ''; // Reset file input
      } catch (error) {
        alert('Error al importar el mazo. Verifica que el archivo sea un JSON de Machucho.');
        console.error('Import error:', error);
      }
    };
    reader.readAsText(file);
  };

//  const formatSetName = (setName) => {
//    return setName.split('_').map(word =>
//      word.charAt(0).toUpperCase() + word.slice(1)
//    ).join(' ');
//  };

  const getDeckStatistics = () => {
    if (!currentDeck || currentDeck.cards.length === 0) return null;
    const typeCount = {};
    const costCount = {};
    const raceCount = {};
    const strategyCount = {};

    currentDeck.cards.forEach(card => {
      const type = card.type || 'Desconocido';
      typeCount[type] = (typeCount[type] || 0) + card.quantity;

      const cost = card.cost !== null && card.cost !== undefined ? card.cost : 'Es un oro';
      costCount[cost] = (costCount[cost] || 0) + card.quantity;

      if (card.race) {
        raceCount[card.race] = (raceCount[card.race] || 0) + card.quantity;
      }
/*
I've using this count for strategy.card, but it wont count cards
with two or more strings as values.
I went with this for now.. the eachCard would help with this, but below
is the old function I used.
      if (card.strategy) {
        strategyCount[card.strategy] = (strategyCount[card.strategy] || 0) + card.quantity;
      }
*/

        if (Array.isArray(card.strategy)) {
            card.strategy.forEach(s => {
            strategyCount[s] = (strategyCount[s] || 0) + card.quantity;
            });
        }
    });

    return { typeCount, costCount, raceCount, strategyCount };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="relative bg-green-600 text-white shadow-md">
        <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Logo"
            className="absolute top-6 right-6 w-24 h-24 object-contain opacity-60 pointer-events-none z-10"
        />
      <div className="max-w-6xl mx-auto px-4 py-6 relative z-20">
          <h1 className="text-3xl font-bold">Machucho</h1>
          <p className="text-white-100 mt-14">Un constructor de Mazos para Mitos y Leyendas.</p>
          <p className="text-white-100 mt-1">(Incluye algunas estadísticas para el meta análisis!)</p>
          {loading && <p className="text-gray-200 text-sm mt-2">Cargando cartas...</p>}
          {!loading && <p className="text-gray-200 text-sm mt-2">{cardsDatabase.length} cartas disponibles</p>}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {!currentDeck ? (
          // Deck Creation View
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md mx-auto">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">Crear Nuevo Mazo</h2>
            <input
              type="text"
              placeholder="Nombre del mazo"
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              onKeyPress={(e) => e.key === 'Enter' && createDeck()}
            />
            <button
              onClick={createDeck}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <Plus size={20} />
              Crear Mazo
            </button>

            {/* Import Deck Button */}
            <div className="mt-4">
              <label className="w-full bg-gray-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2 cursor-pointer">
                <Download size={20} />
                Importar Mazo
                <input
                  type="file"
                  accept=".json"
                  onChange={importDeck}
                  className="hidden"
                />
              </label>
            </div>

            {decks.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Mazos Creados</h3>
                <div className="space-y-2">
                  {decks.map(deck => (
                    <div key={deck.id} className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentDeck(deck)}
                        className="flex-1 text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-3"
                      >
                        <List size={20} className="text-gray-600" />
                        <div>
                          <div className="font-medium text-gray-800">{deck.name}</div>
                          <div className="text-sm text-gray-600">
                            {deck.cards.reduce((sum, c) => sum + c.quantity, 0)} cartas
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => deleteDeck(deck.id)}
                        className="p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar mazo"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Deck Builder View
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">{currentDeck.name}</h2>
                  <p className="text-gray-600 mt-1">
                    {getTotalCards()} / 50 cartas
                  </p>
                </div>
                    <div className="flex gap-2">
                    <button
                        onClick={exportDeck}
                        className="px-4 py-2 bg-gray-600 text-white hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
                        title="Exportar mazo"
                        >
                        <Upload size={18} />
                        Exportar
                        </button>
                <button
                  onClick={() => setCurrentDeck(null)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                >
                  Volver
                </button>
              </div>
          </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    getTotalCards() > 50 ? 'bg-red-500' : 'bg-blue-600'
                  }`}
                  style={{ width: `${Math.min((getTotalCards() / 50) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Statistics Box */}
            {currentDeck.cards.length > 0 && (() => {
              const stats = getDeckStatistics();
              return (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Estadísticas del Mazo</h3>

                  <div className="grid md:grid-cols-3 gap-6">
                    {/* Type Distribution */}
                    <div>
                      <h4 className="text-base font-medium text-black-700 mb-3">Distribución por tipo</h4>
                      <div className="space-y-2">
                        {Object.entries(stats.typeCount).sort(([, a], [, b]) => b - a).map(([type, count]) => (
                            <div key={type} className="flex items-center justify-between">
                              <span className="text-sm italic text-gray-700">∘ {type}</span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${(count / getTotalCards()) * 100}%` }} />
                                </div>
                                <span className="text-sm font-medium text-gray-600 w-8 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Cost Distribution */}
                    <div>
                      <h4 className="text-base font-medium text-black-700 mb-3">Distribución por costo</h4>
                      <div className="space-y-2">
                        {Object.entries(stats.costCount).sort(([a], [b]) => {
                            if (a === 'Es un oro') return 1;
                            if (b === 'Es un oro') return -1;
                            return Number(a) - Number(b);
                          }).map(([cost, count]) => (
                            <div key={cost} className="flex items-center justify-between">
                              <span className="text-sm italic text-gray-700">∘ {cost} </span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: `${(count / getTotalCards()) * 100}%` }} />
                                </div>
                                <span className="text-sm font-medium text-gray-600 w-8 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Race Distribution */}
                    <div>
                      <h4 className="text-base font-medium text-black-700 mb-3">Razas de Aliados</h4>
                      <div className="space-y-2">
                        {Object.entries(stats.raceCount).sort(([, a], [, b]) => b - a).map(([race, count]) => (
                            <div key={race} className="flex items-center justify-between">
                              <span className="text-sm italic text-gray-700">∘ {race}</span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(count / getTotalCards()) * 100}%` }} />
                                </div>
                                <span className="text-sm font-medium text-gray-600 w-8 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Strategy Distribution - FIXED VARIABLE NAME HERE */}
                    <div>
                      <h4 className="text-base font-medium text-black-700 mb-3">Estrategias en el Mazo</h4>
                      <div className="space-y-2">
                        {Object.entries(stats.strategyCount)
                          .sort(([, a], [, b]) => b - a)
                          .map(([strategy, count]) => (
                            <div key={strategy} className="flex items-center justify-between">
                              <span className="text-sm italic text-gray-700">∘ {strategy}</span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-orange-500 h-2 rounded-full"
                                    style={{ width: `${(count / getTotalCards()) * 100}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium text-gray-600 w-8 text-right">
                                  {count}
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                  </div>
                </div>
              );
            })()}

            {/* Add Card Button */}
            {!showAddCard && (
              <button
                onClick={() => setShowAddCard(true)}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 mb-6 shadow-md disabled:bg-gray-400"
              >
                <Plus size={24} />
                Agregar Carta
              </button>
            )}

            {/* Add Card Form */}
            {showAddCard && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Nueva Carta</h3>

                {cardsDatabase.length === 0 && !loading && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-yellow-800 text-sm">No se encontraron cartas.</p>
                  </div>
                )}

                {/* Filters */}
                <div className={`grid gap-4 mb-4 ${filterType === 'aliado' ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
                  {/* 1. Set Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por Edición</label>
                    <select
                      value={filterSet}
                      onChange={(e) => setFilterSet(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Todas las Ediciones</option>
                      {availableSets.map(setName => (
                        <option key={setName} value={setName}>
                          {cleanName(setName)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 2. Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por Tipo</label>
                    <select
                      value={filterType}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFilterType(newType);
                        if (newType !== 'aliado') setFilterRace('all');
                        }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Todos</option>
                      <option value="aliado">Aliado</option>
                      <option value="talismán">Talismán</option>
                      <option value="arma">Arma</option>
                      <option value="tótem">Tótem</option>
                      <option value="oro">Oro</option>
                    </select>
                  </div>

                  {/* 3. Race Filter */}
                  {filterType === 'aliado' && (
                  <div className='animate-in fade-in duration-300'>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por Raza</label>
                    <select
                        value={filterRace}
                        onChange={(e) => setFilterRace(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">Todas las razas</option>
                            {availableRaces.map(race => (
                        <option key={race} value={race}>{race}</option>
                    ))}
                    </select>
                  </div>
                )}
                </div>

                {/* Search Input */}
                <div className="mb-4 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Buscar carta</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setShowDropdown(true);
                        setSelectedCard(null); // Clear selected on search change
                      }}
                      onFocus={() => setShowDropdown(true)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Escribe para buscar..."
                    />
                  </div>

                  {/* Dropdown */}
                  {showDropdown && filteredCards.length > 0 && searchTerm && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredCards.slice(0, 50).map(card => (
                        <button
                          key={card.id}
                          onClick={() => selectCard(card)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-800">{card.name}</div>
                          <div className="text-sm text-gray-600">
                            {cleanName(card.set)} • {card.type} • {card.rarity}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Card Preview (Wrapped to prevent null error) */}
                {selectedCard && (
                  <>
                    <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl flex flex-col md:flex-row gap-6 animate-in fade-in slide-in-from-top-4 duration-300">
                      {/* Card Image */}
                      <div className="w-44 shrink-0 mx-auto">
                        <img
                          src={selectedCard.image_path}
                          alt={selectedCard.name}
                          className="w-full h-auto rounded-lg shadow-lg hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/200x280?text=Imagen+no+disponible";
                          }}
                        />
                      </div>

                      {/* Card Details */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="text-xl font-bold text-gray-900">{selectedCard.name}</h4>
                          <span className="text-xs font-bold uppercase px-2 py-1 bg-white border border-blue-200 rounded text-blue-700">
                            {selectedCard.rarity}
                          </span>
                        </div>

                        <p className="text-blue-600 text-sm font-semibold mb-3">
                          {selectedCard.type} {selectedCard.race ? `• ${selectedCard.race}` : ''}
                        </p>
                        <p className="text-gray-500 text-xs mb-2 uppercase">
                          {selectedCard.edition} • {cleanName(selectedCard.set)}
                        </p>

                        <div className="text-sm text-gray-700 bg-white/80 p-3 rounded-lg border border-blue-100 italic leading-snug mb-4 shadow-inner">
                          {selectedCard.ability || "Sin habilidad especial."}
                        </div>

                        {selectedCard.type === 'Aliado' && (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-2 rounded border border-blue-50 text-center">
                              <span className="block text-[10px] font-bold text-gray-400 uppercase">Costo</span>
                              <span className="text-lg font-bold text-gray-800">{selectedCard.cost}</span>
                            </div>
                            <div className="bg-white p-2 rounded border border-blue-50 text-center">
                              <span className="block text-[10px] font-bold text-gray-400 uppercase">Fuerza</span>
                              <span className="text-lg font-bold text-gray-800">{selectedCard.attack}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Quantity Input */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cantidad (máx: {getMaxCopies(selectedCard.rarity)})
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={getMaxCopies(selectedCard.rarity)}
                        value={cardQuantity}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setCardQuantity(val);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={addCardToDeck}
                    disabled={!selectedCard}
                    className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                      selectedCard
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Agregar
                  </button>
                  <button
                    onClick={cancelAddCard}
                    className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Cards List */}
            {currentDeck.cards.length > 0 ? (
              <div className="space-y-4">
                {['Aliado', 'Talismán', 'Arma', 'Tótem', 'Oro'].map(type => {
                  const cards = getCardsByType(type);
                  if (cards.length === 0) return null;

                  return (
                    <div key={type} className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold mb-4 text-gray-800">
                        {type} ({cards.reduce((sum, c) => sum + c.quantity, 0)})
                      </h3>
                      <div className="space-y-4">
                        {cards.map(card => (
                          <div key={card.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">{card.name}</div>
                              <div className="text-sm text-gray-600">
                                {cleanName(card.set)} / {card.race ? `${card.race} •` : ''} Fuerza: {card.attack || '-'} / Coste: {card.cost || '-'}
                                <div className="text-sm text-gray-600 mt-1">
                                    <span className="italic">{card.ability && card.ability.length > 200 ? card.ability.substring(0, 200) + '...' : card.ability}</span>
                                </div>
                                <div className="text-xs font-bold text-blue-600 mt-1">
                                    {card.rarity}  x{card.quantity}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => removeCard(card.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
                <p className="text-lg">No hay cartas en este mazo</p>
                <p className="text-sm mt-2">Agrega tu primera carta para comenzar</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}